import { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, InfoWindow, Marker } from "@react-google-maps/api";
import { FiX } from "react-icons/fi";
import useGoogleMapsLoader from "../../../Hooks/useGoogleMapsLoader";

const containerStyle = { width: "100%", height: "100%" };

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  clickableIcons: false,
};

const mapWrapperStyle = {
  height: "65vh",
  minHeight: 380,
  width: "100%",
  borderRadius: 12,
  overflow: "hidden",
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
};

const pickerWrapperStyle = (height) => ({
  height,
  width: "100%",
  borderRadius: 8,
  overflow: "hidden",
  border: "1px solid #E0E0E0",
  background: "#F8FAFC",
});

const infoRowGridStyle = {
  display: "grid",
  gridTemplateColumns: "160px 1fr",
  gap: 8,
};

const infoLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: "#64748b",
};

const infoValueStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: "#1e293b",
  wordBreak: "break-word",
};

const infoValueBoldStyle = {
  ...infoValueStyle,
  fontWeight: 800,
  color: "#0f172a",
};

const safeString = (value) => {
  if (value == null) return "";
  return typeof value === "string" ? value : String(value);
};

const normalizeLatLng = (row) => {
  const latRaw = row?.latitude ?? row?.Latitude;
  const lngRaw = row?.longitude ?? row?.Longitude;
  const lat = typeof latRaw === "string" ? parseFloat(latRaw) : Number(latRaw);
  const lng = typeof lngRaw === "string" ? parseFloat(lngRaw) : Number(lngRaw);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
};

async function reverseGeocode(lat, lng) {
  const googleMaps = window.google?.maps;
  if (googleMaps?.Geocoder) {
    const geocoder = new googleMaps.Geocoder();
    const result = await new Promise((resolve) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.length) {
          resolve(results[0]?.formatted_address ?? "");
        } else {
          resolve("");
        }
      });
    });
    if (result) return result;
  }

  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`,
      { headers: { Accept: "application/json" } }
    );
    const json = await res.json();
    return json?.display_name ?? "";
  } catch {
    return "";
  }
}

const InfoRow = ({ label, value, bold = false }) => (
  <div style={infoRowGridStyle}>
    <div style={infoLabelStyle}>{label}</div>
    <div style={bold ? infoValueBoldStyle : infoValueStyle}>{value}</div>
  </div>
);

const MapInfoWindow = ({
  position,
  showInfo,
  onClose,
  className,
  children,
}) => {
  if (!showInfo || !position || !window.google?.maps) return null;

  return (
    <InfoWindow
      position={position}
      onCloseClick={onClose}
      options={{
        pixelOffset: new window.google.maps.Size(0, -36),
        headerDisabled: true,
      }}
      onLoad={(infoWindow) => {
        window.google.maps.event.addListener(infoWindow, "domready", () => {
          const content = document.querySelector(`.${className}`);
          const shell = content?.closest(".gm-style-iw");
          const chrome = shell?.querySelector(".gm-style-iw-ch");
          const body = shell?.querySelector(".gm-style-iw-c");
          if (chrome) {
            chrome.style.paddingTop = "0";
            chrome.style.minHeight = "0";
          }
          if (body) body.style.padding = "6px 10px 10px";
        });
      }}
    >
      <div
        className={className}
        style={{
          position: "relative",
          minWidth: 280,
          maxWidth: 480,
          paddingRight: 24,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          title="Close"
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: 28,
            width: 28,
            border: "none",
            borderRadius: 8,
            background: "transparent",
            color: "#64748b",
            cursor: "pointer",
          }}
        >
          <FiX size={15} />
        </button>
        <div style={{ display: "grid", gap: 8 }}>{children}</div>
      </div>
    </InfoWindow>
  );
};

/** Interactive map for add/edit — click to pick location with info popup on marker. */
const RfidMapPicker = ({
  latitude,
  longitude,
  deviceName = "",
  locationLandmark = "",
  height = "320px",
  showDefaultMap = true,
  defaultCenter = { lat: 28.613939, lng: 77.209021 },
  locationLoading = false,
  onLocationSelect,
}) => {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const [showInfo, setShowInfo] = useState(false);
  const [resolvedAddress, setResolvedAddress] = useState("");

  const position = useMemo(() => {
    const lat = typeof latitude === "string" ? parseFloat(latitude) : Number(latitude);
    const lng = typeof longitude === "string" ? parseFloat(longitude) : Number(longitude);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
    return null;
  }, [latitude, longitude]);

  const mapCenter = useMemo(() => {
    if (position) return position;
    if (showDefaultMap && defaultCenter) {
      const lat =
        typeof defaultCenter.lat === "string"
          ? parseFloat(defaultCenter.lat)
          : Number(defaultCenter.lat);
      const lng =
        typeof defaultCenter.lng === "string"
          ? parseFloat(defaultCenter.lng)
          : Number(defaultCenter.lng);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        return { lat, lng };
      }
    }
    return null;
  }, [position, showDefaultMap, defaultCenter]);

  const markerPosition = position || mapCenter;

  const name = safeString(deviceName) || "—";
  const addressFromField = safeString(locationLandmark) || "";
  const address = addressFromField || resolvedAddress || "—";

  const latLngLabel = useMemo(() => {
    if (!markerPosition) return "—";
    return `${markerPosition.lat.toFixed(6)}, ${markerPosition.lng.toFixed(6)}`;
  }, [markerPosition]);

  useEffect(() => {
    if (!mapRef.current || !mapCenter) return;
    mapRef.current.panTo(mapCenter);
  }, [mapCenter?.lat, mapCenter?.lng]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !containerRef.current) return;

    const triggerResize = () => {
      if (!mapRef.current || !window.google?.maps?.event) return;
      window.google.maps.event.trigger(mapRef.current, "resize");
      if (mapCenter) {
        mapRef.current.panTo(mapCenter);
        if (mapRef.current.getZoom() < 18) mapRef.current.setZoom(18);
      }
    };

    triggerResize();
    const timer = setTimeout(triggerResize, 300);

    const observer = new ResizeObserver(() => {
      triggerResize();
    });
    observer.observe(containerRef.current);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [isLoaded, mapCenter?.lat, mapCenter?.lng]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!markerPosition) return;
      if (addressFromField && addressFromField.length >= 6) {
        setResolvedAddress("");
        return;
      }
      const addr = await reverseGeocode(markerPosition.lat, markerPosition.lng);
      if (!cancelled) setResolvedAddress(addr);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [markerPosition?.lat, markerPosition?.lng, addressFromField]);

  const handleMapClick = async (event) => {
    if (!onLocationSelect) return;

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const latStr = lat.toFixed(6);
    const lngStr = lng.toFixed(6);
    const addr = await reverseGeocode(lat, lng);
    onLocationSelect(latStr, lngStr, addr);
    setShowInfo(true);
  };

  if (loadError) {
    return <div className="text-danger p-3">Unable to load map.</div>;
  }

  if (!mapCenter) {
    return (
      <div
        className="p-3 text-muted d-flex align-items-center justify-content-center"
        style={pickerWrapperStyle(height)}
      >
        {locationLoading
          ? "Detecting current location..."
          : "Enter valid latitude and longitude to preview the map."}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={pickerWrapperStyle(height)}>
      <div style={{ position: "relative", height: "100%", width: "100%" }}>
        {!isLoaded ? (
          <div
            className="d-flex align-items-center justify-content-center text-muted"
            style={{ height: "100%", width: "100%", fontSize: 14 }}
          >
            Loading map...
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={18}
            options={mapOptions}
            onClick={handleMapClick}
            onLoad={(map) => {
              mapRef.current = map;
              map.panTo(mapCenter);
              if (map.getZoom() < 18) map.setZoom(18);
            }}
          >
            <Marker
              position={markerPosition}
              onClick={() => setShowInfo(true)}
            />
            <MapInfoWindow
              position={markerPosition}
              showInfo={showInfo}
              onClose={() => setShowInfo(false)}
              className="rfid-map-picker-infowindow"
            >
              <InfoRow label="Name :" value={name} />
              <InfoRow label="Address :" value={address} />
              <InfoRow label="Lat/Lon :" value={latLngLabel} bold />
            </MapInfoWindow>
          </GoogleMap>
        )}
      </div>
    </div>
  );
};

/** Map + info panel for a RFID row (used in modal popup). */
export function RfidMapContent({ row }) {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const mapRef = useRef(null);

  const [showInfo, setShowInfo] = useState(true);
  const [resolvedAddress, setResolvedAddress] = useState("");

  const position = useMemo(() => normalizeLatLng(row), [row]);

  const categoryName =
    safeString(row?.rfidCategoryName ?? row?.assetCategoryName) || "—";
  const deviceName =
    safeString(row?.rfidDeviceName ?? row?.RfidDeviceName) || "—";
  const addressFromRow =
    safeString(row?.locationLandmark ?? row?.LocationLandmark) || "";
  const address = resolvedAddress || addressFromRow || "—";

  const latLngLabel = useMemo(() => {
    if (!position) return "—";
    return `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`;
  }, [position]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!position) return;
      if (addressFromRow && addressFromRow.length >= 6) {
        setResolvedAddress("");
        return;
      }
      const addr = await reverseGeocode(position.lat, position.lng);
      if (!cancelled) setResolvedAddress(addr);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [position?.lat, position?.lng, addressFromRow]);

  if (!row || !position) {
    return (
      <div style={{ padding: 16, fontSize: 14, color: "#475569" }}>
        Coordinates are not available for this RFID record.
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: 16, fontSize: 14, color: "#dc2626" }}>
        Unable to load map.
      </div>
    );
  }

  return (
    <div style={mapWrapperStyle}>
      <div style={{ position: "relative", height: "100%", width: "100%" }}>
        {!isLoaded ? (
          <div
            style={{
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              color: "#64748b",
            }}
          >
            Loading map...
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={position}
            zoom={18}
            options={mapOptions}
            onLoad={(map) => {
              mapRef.current = map;
              map.panTo(position);
              if (map.getZoom() < 18) map.setZoom(18);
            }}
          >
            <Marker position={position} onClick={() => setShowInfo(true)} />
            <MapInfoWindow
              position={position}
              showInfo={showInfo}
              onClose={() => setShowInfo(false)}
              className="rfid-map-infowindow"
            >
              <InfoRow label="RFID Category :" value={categoryName} />
              <InfoRow label="RFID Device Name :" value={deviceName} />
              <InfoRow label="Address :" value={address} />
              <InfoRow label="Lat/Lon :" value={latLngLabel} bold />
            </MapInfoWindow>
          </GoogleMap>
        )}
      </div>
    </div>
  );
}

export default RfidMapPicker;
